import React from 'react';
import { GlassCard, ProgressBar } from '@/design-system';
import { Badge } from '@/components/ui';
import { Users, Target, DollarSign } from 'lucide-react';
import { formatTeamWeeks, formatCurrencyFull, formatPercent } from '@/utils/formatters';

interface ContributingProgram {
  programId: string;
  programName: string;
  category: string;
  role: 'primary' | 'contributing';
  allocatedTeamWeeks: number;
  epicCount: number;
  utilizationPercentage: number;
  teamCount: number;
}

interface ContributingProgramsViewProps {
  contributingPrograms: ContributingProgram[] | null;
  isLoading: boolean;
}

const ContributingProgramsView: React.FC<ContributingProgramsViewProps> = ({
  contributingPrograms,
  isLoading,
}) => {
  const formatCostFromTeamWeeks = (teamWeeks: number, hourlyRate: number = 89) => {
    const cost = teamWeeks * 40 * hourlyRate;
    return formatCurrencyFull(cost);
  };

  const getRoleBadge = (role: string) => {
    return role === 'primary' ? (
      <Badge variant="primary">Primary</Badge>
    ) : (
      <Badge variant="secondary">Contributing</Badge>
    );
  };

  // All programs are TECH now, so we can simplify the category badge
  const getCategoryBadge = () => {
    return <Badge variant="info">Tech</Badge>;
  };

  const getUtilizationColor = (percentage: number): 'blue' | 'emerald' | 'amber' | 'rose' => {
    if (percentage > 100) return 'rose';
    if (percentage > 90) return 'amber';
    if (percentage > 70) return 'emerald';
    return 'blue';
  };

  const getUtilizationTextColor = (percentage: number) => {
    if (percentage > 100) return 'text-accent-rose';
    if (percentage > 90) return 'text-accent-amber';
    if (percentage > 70) return 'text-accent-emerald';
    return 'text-accent-blue';
  };

  if (isLoading) {
    return (
      <GlassCard hover padding="lg">
        <div className="animate-pulse">
          <div className="h-6 bg-text-tertiary/20 rounded w-1/2 mb-4"></div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-center space-x-4 p-4 bg-surface-secondary rounded-lg">
                <div className="w-10 h-10 bg-text-tertiary/20 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-text-tertiary/20 rounded w-3/4"></div>
                  <div className="h-3 bg-text-tertiary/20 rounded w-1/2"></div>
                </div>
                <div className="w-20 h-6 bg-text-tertiary/20 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </GlassCard>
    );
  }

  if (!contributingPrograms || contributingPrograms.length === 0) {
    return (
      <GlassCard hover padding="lg">
        <h3 className="text-lg font-semibold text-foreground mb-4">Contributing Programs</h3>
        <div className="text-center text-muted-foreground/60 py-8">
          <Users className="w-12 h-12 mx-auto mb-4" />
          <p>No contributing programs</p>
          <p className="text-sm mt-2">This IS has no program allocations</p>
        </div>
      </GlassCard>
    );
  }

  const totalAllocation = contributingPrograms.reduce((sum, program) => sum + program.allocatedTeamWeeks, 0);
  const primaryPrograms = contributingPrograms.filter(p => p.role === 'primary');
  const contributingProgramsOnly = contributingPrograms.filter(p => p.role === 'contributing');

  return (
    <GlassCard hover padding="lg">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Contributing Programs</h3>
          <p className="text-sm text-muted-foreground/60">
            Programs delivering capacity to this IS
          </p>
        </div>
        <div className="text-right">
          <div className="text-lg font-bold text-foreground tabular-nums">
            {formatTeamWeeks(totalAllocation)}
          </div>
          <div className="text-sm text-muted-foreground/60">Total Allocation</div>
        </div>
      </div>

      <div className="space-y-4">
        {contributingPrograms
          .sort((a, b) => b.allocatedTeamWeeks - a.allocatedTeamWeeks)
          .map((program, index) => (
            <div
              key={program.programId}
              className="p-4 bg-surface-secondary rounded-xl hover:bg-surface-tertiary transition-colors"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-muted-foreground/60">
                      #{index + 1}
                    </span>
                    <span className="font-medium text-foreground">
                      {program.programName}
                    </span>
                  </div>
                  {getRoleBadge(program.role)}
                  {getCategoryBadge()}
                </div>
                <div className="text-right">
                  <div className="font-medium text-foreground tabular-nums">
                    {formatTeamWeeks(program.allocatedTeamWeeks)}
                  </div>
                  <div className="text-xs text-muted-foreground/60">
                    {formatPercent(totalAllocation > 0 ? (program.allocatedTeamWeeks / totalAllocation) * 100 : 0)} of total
                  </div>
                </div>
              </div>

              {/* Allocation Bar */}
              <div className="mb-3">
                <ProgressBar
                  value={totalAllocation > 0 ? (program.allocatedTeamWeeks / totalAllocation) * 100 : 0}
                  color={program.role === 'primary' ? 'blue' : 'violet'}
                  size="sm"
                  showShimmer
                  gradient
                />
              </div>

              {/* Program Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="flex items-center space-x-2">
                  <Target className="w-4 h-4 text-accent-blue" />
                  <div>
                    <div className="text-foreground tabular-nums">{program.epicCount}</div>
                    <div className="text-xs text-muted-foreground/60">EPICs</div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Users className="w-4 h-4 text-accent-emerald" />
                  <div>
                    <div className="text-foreground tabular-nums">{program.teamCount}</div>
                    <div className="text-xs text-muted-foreground/60">Teams</div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <DollarSign className="w-4 h-4 text-accent-amber" />
                  <div>
                    <div className="text-foreground tabular-nums">
                      {formatCostFromTeamWeeks(program.allocatedTeamWeeks)}
                    </div>
                    <div className="text-xs text-muted-foreground/60">Cost</div>
                  </div>
                </div>

                <div>
                  <div className={`font-medium tabular-nums ${getUtilizationTextColor(program.utilizationPercentage)}`}>
                    {formatPercent(program.utilizationPercentage)}
                  </div>
                  <div className="text-xs text-muted-foreground/60">Utilization</div>
                </div>
              </div>
            </div>
          ))}
      </div>

      {/* Program Summary */}
      <div className="mt-6 pt-6 border-t border-border-subtle">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-lg font-bold text-foreground tabular-nums">
              {contributingPrograms.length}
            </div>
            <div className="text-xs text-muted-foreground/60">Total Programs</div>
          </div>
          <div>
            <div className="text-lg font-bold text-accent-blue tabular-nums">
              {primaryPrograms.length}
            </div>
            <div className="text-xs text-muted-foreground/60">Primary Programs</div>
          </div>
          <div>
            <div className="text-lg font-bold text-accent-violet tabular-nums">
              {contributingProgramsOnly.length}
            </div>
            <div className="text-xs text-muted-foreground/60">Contributing Programs</div>
          </div>
          <div>
            <div className="text-lg font-bold text-foreground tabular-nums">
              {contributingPrograms.reduce((sum, p) => sum + p.teamCount, 0)}
            </div>
            <div className="text-xs text-muted-foreground/60">Total Teams</div>
          </div>
        </div>
      </div>

      {/* Cross-Program Coordination Alert */}
      {contributingProgramsOnly.length > 2 && (
        <div className="mt-4 p-3 bg-accent-blue-soft border border-accent-blue/20 rounded-lg">
          <div className="flex items-center space-x-2 mb-1">
            <Users className="w-4 h-4 text-accent-blue" />
            <span className="text-sm font-medium text-accent-blue">Multi-Program Coordination</span>
          </div>
          <p className="text-xs text-muted-foreground">
            This IS involves {contributingProgramsOnly.length} contributing programs.
            Ensure proper coordination and communication across program boundaries.
          </p>
        </div>
      )}
    </GlassCard>
  );
};

export default ContributingProgramsView;
